import React, { useState } from 'react'
import api from 'api/api'
import styles from 'components/modals/wallet.module.scss'
import { CreditCard, History, RefreshCw } from 'lucide-react'
import type { AiCreditEntry, AiCreditsStatus } from 'api/useStripeAiBillingStatus'
import {
    cancelExternalCheckout,
    openExternalCheckout,
    prepareExternalCheckout,
} from 'utils/nativeApp'

const RELOAD_OPTIONS_CENTS = [1000, 2000, 5000]
const THRESHOLD_OPTIONS_CENTS = [200, 500, 1000]

const entryLabel = (entry: AiCreditEntry) => {
    if (entry.kind === 'hold') {
        if (entry.status === 'captured') return 'Generation'
        if (entry.status === 'released') return 'Reserved, returned'
        return 'Reserved'
    }
    return KIND_LABELS[entry.kind] || entry.kind
}

const KIND_LABELS: Record<string, string> = {
    topup: 'Added',
    charge: 'Generation',
    release: 'Returned',
    refund: 'Refund',
    adjustment: 'Adjustment',
}

// Always two decimals for amounts ($1.80); whole dollars for top-up buttons.
const usd = (cents: number) => `$${((cents || 0) / 100).toFixed(2)}`
const dollars = (cents: number) => `$${Math.round((cents || 0) / 100)}`

// Prepaid USD credit for AI generations: balance, top-ups and auto-reload.
export default function WalletUsdCredits({
    credits,
    onChange,
}: {
    credits: AiCreditsStatus
    onChange: (credits: AiCreditsStatus) => void
}) {
    const [buying, setBuying] = useState<number | null>(null)
    const [saving, setSaving] = useState(false)

    const buy = async (amountCents: number) => {
        const checkout = prepareExternalCheckout()
        if (checkout.requiresPopup && !checkout.popup) {
            alert('Could not open Stripe in your browser. Please try again.')
            return
        }
        try {
            setBuying(amountCents)
            const resp = await api.createStripeAiBillingCheckoutSession(amountCents)
            if (resp?.url) {
                if (!openExternalCheckout(resp.url, checkout)) {
                    cancelExternalCheckout(checkout)
                    alert('Could not open Stripe in your browser. Please try again.')
                }
            } else {
                cancelExternalCheckout(checkout)
                alert(resp?.error || 'Could not start checkout')
            }
        } catch (e) {
            cancelExternalCheckout(checkout)
            alert('Could not start checkout')
        } finally {
            setBuying(null)
        }
    }

    const saveReload = async (settings: {
        enabled?: boolean
        reload_cents?: number
        threshold_cents?: number
    }) => {
        try {
            setSaving(true)
            const resp = await api.setStripeAiAutoReload(settings)
            if (resp?.ok && resp.credits) onChange(resp.credits)
            else alert(resp?.error || 'Could not update auto-reload')
        } catch (e) {
            alert('Could not update auto-reload')
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            <div className={styles.stripeCard}>
                <div className={styles.depositHeader}>
                    <CreditCard size={20} /> AI credits
                </div>
                <div className={styles.stripeRow}>
                    <div className={styles.stripeKey}>Balance</div>
                    <div
                        className={styles.stripeValue}
                        style={{ color: 'var(--color-brand-usd-green)', fontSize: 20 }}
                    >
                        {usd(credits.balance_cents)}
                    </div>
                </div>
                {credits.card && (
                    <div className={styles.stripeRow}>
                        <div className={styles.stripeKey}>Card</div>
                        <div className={styles.stripeValue}>{credits.card}</div>
                    </div>
                )}
                <p style={{ color: 'var(--color-text-secondary)', margin: '12px 0' }}>
                    Pay for AI generations from a prepaid balance. No monthly fee.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                    {(credits.topup_options_cents || [1000, 2000, 5000]).map(cents => (
                        <button
                            key={cents}
                            className={styles.sendBtn}
                            style={{
                                width: 'auto',
                                padding: '10px 0',
                                background: 'var(--color-brand-usd-green)',
                            }}
                            disabled={buying !== null}
                            onClick={() => buy(cents)}
                        >
                            {buying === cents ? '...' : `Add ${dollars(cents)}`}
                        </button>
                    ))}
                </div>
            </div>

            <div className={styles.stripeCard}>
                <div className={styles.depositHeader}>
                    <RefreshCw size={20} /> Auto-reload
                </div>
                <div className={styles.stripeRow}>
                    <div className={styles.stripeKey}>Reload when low</div>
                    <label className={styles.stripeValue}>
                        <input
                            type="checkbox"
                            checked={!!credits.auto_reload && !credits.reload_failed_at}
                            disabled={saving}
                            onChange={e => saveReload({ enabled: e.target.checked })}
                        />
                    </label>
                </div>
                <div className={styles.stripeRow}>
                    <div className={styles.stripeKey}>Add</div>
                    <select
                        className={styles.stripeValue}
                        value={credits.reload_cents}
                        disabled={saving}
                        onChange={e => saveReload({ reload_cents: Number(e.target.value) })}
                    >
                        {RELOAD_OPTIONS_CENTS.map(cents => (
                            <option key={cents} value={cents}>
                                {dollars(cents)}
                            </option>
                        ))}
                    </select>
                </div>
                <div className={styles.stripeRow}>
                    <div className={styles.stripeKey}>When balance is below</div>
                    <select
                        className={styles.stripeValue}
                        value={credits.threshold_cents}
                        disabled={saving}
                        onChange={e => saveReload({ threshold_cents: Number(e.target.value) })}
                    >
                        {THRESHOLD_OPTIONS_CENTS.map(cents => (
                            <option key={cents} value={cents}>
                                {dollars(cents)}
                            </option>
                        ))}
                    </select>
                </div>
                {credits.reload_failed_at ? (
                    <p style={{ color: 'var(--color-text-secondary)', marginTop: 12 }}>
                        Auto-reload is paused:{' '}
                        {(credits.reload_failure || 'your card was declined').replace(/\.$/, '')}.
                        Add credits or turn auto-reload back on to try again.
                    </p>
                ) : (
                    !credits.card_saved && (
                        <p style={{ color: 'var(--color-text-secondary)', marginTop: 12 }}>
                            Auto-reload starts after your first purchase saves a card.
                        </p>
                    )
                )}
            </div>

            <div className={styles.stripeEvents}>
                <div className={styles.depositHeader}>
                    <History size={20} /> Recent activity
                </div>
                {(credits.entries || []).slice(0, 10).map(entry => (
                    <div key={entry.id} className={styles.stripeEvent}>
                        <div style={{ color: 'var(--color-text-secondary)', fontSize: '12px' }}>
                            {new Date(entry.created_at).toLocaleDateString()}
                        </div>
                        <div style={{ fontWeight: 600 }}>
                            {entry.amount_cents > 0 ? '+' : '−'}
                            {usd(Math.abs(entry.amount_cents))}
                        </div>
                        <div className={styles.stripeEventStatus}>{entryLabel(entry)}</div>
                    </div>
                ))}
            </div>
        </>
    )
}
